// Whiteboard — the board itself: an endless canvas you draw on with the pen,
// highlighter, shapes, lines, arrows, text, sticky notes and pictures; select
// (click, Shift, box, lasso), move, resize, rotate, group, lock, order;
// undo / redo; pan and zoom (wheel, pinch, Space-drag); a stylus draws with
// its pressure, its eraser end rubs out (the tool stays), and once a pen has
// been used on the device only the pen draws: a hand resting on the screen is
// ignored and two fingers move and zoom the view (engine/input.js). Everyone
// with the work item open draws on the same board live (sync.js): their
// cursors, selections and strokes in the making show in their colour.
//
// Rendering is SVG: the elements' primitives (engine/scene.js) at the
// view's zoom, the selection's handles and other people's cursors on top in
// screen space. A gesture changes only a PREVIEW of the board; the change is
// committed (saved, undoable, sent to everyone) when the pointer lifts.

import React, { memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  makeElement, normalizeLinear, fitText, fitNote, primitives, opsToSvg, dashArray, bounds, boundsOf, hits, touchesSegment,
  penLodPath, insideLasso, moveBy, rotateAbout, resizeOne, scaleWithin, movePoint, worldPoints, toWorld, toLocal, center, textBox, newId,
  fitSymbol, dimEnds, dimMoveEnd, dimOffsetPoint, dimSetOffset, leaderEnd, moveLeader, bodyBox, localBox,
  LINEAR, HAS_TEXT, SHAPES, isDrawing, ridersOf,
} from '@modules/database/whiteboard/frontend/engine/scene';
import { drawingLabel, drawingsOf } from '@modules/database/whiteboard/frontend/engine/export';
import { layoutSymbol } from '@modules/database/whiteboard/frontend/engine/symbols';
import whiteboardApi from '@modules/database/whiteboard/frontend/api';
import { ZebraRun, ShopZebra } from '@modules/database/whiteboard/frontend/ZebraArt';
import AttachmentPicker from '@modules/database/whiteboard/frontend/AttachmentPicker';
import SymbolPanel from '@modules/database/whiteboard/frontend/SymbolPanel';
import { useLookAndFeel } from '@platform/app-shell/frontend/lookAndFeel';
import productLogo from '@app/assets/zebra-stride-logo-light.png';
import { simulatedPressure } from '@modules/database/whiteboard/frontend/engine/freehand';
import { pointerRole } from '@modules/database/whiteboard/frontend/engine/input';
import { simplify, boxesOverlap, boxContains, rotate as rot, clamp, expandBox, corners } from '@modules/database/whiteboard/frontend/engine/geometry';
import { layoutText, FAMILIES, LINE_HEIGHT } from '@modules/database/whiteboard/frontend/engine/text';
import { Icon, IconButton, LayerContext, Avatar, Tip, Popover } from '@modules/database/whiteboard/frontend/ui';
import { ToolRail, StyleBar, KEYMAP, SHAPE_KEYS } from '@modules/database/whiteboard/frontend/Toolbar';
import ExportDialog from '@modules/database/whiteboard/frontend/ExportDialog';

const MIN_Z = 0.1;
const MAX_Z = 8;
const DRAG_PX = 4;
const HANDLE_PX = 9;
const ROTATE_GAP = 26;
const CLIP_MARK = 'stride-whiteboard:';
const STYLE_KEY = 'stride.whiteboard.style';
const DEFAULT_STYLE = {
  stroke: '#1e293b', fill: 'transparent', width: 3, penWidth: 4, highlightWidth: 20, dash: 'solid', fontSize: 24, font: 'sans',
  align: 'left', bold: false, noteColor: '#fef08a', highlight: '#facc15', startHead: 'none', endHead: 'arrow', shape: 'rect',
  symbol: 'fcf', symbolSize: 16,
};
const PAGE_GAP = 80;   // between the pages of a PDF laid side by side
// a drawing from a PDF (it has pages), not a picture
const isPdfDoc = (el) => !!el?.doc && (el.doc.pages > 1 || /\.pdf$/i.test(el.doc.name || '') || !/\.(png|jpe?g|gif|webp|bmp|tiff?|heic)$/i.test(el.doc.name || ''));
const PANEL_KEY = 'stride.whiteboard.panelPos';
const TOUCH_KEY = 'stride.whiteboard.touchDraws';   // this person's choice on this device: fingers draw too, or only the pen
const PEN_KEY = 'stride.whiteboard.penSeen';        // a pen has been used on this device
const HAS_TOUCH = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
const store = {
  get(k, d) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* storage may be blocked */ } },
};
let clipboard = null;   // what was last copied on this page (also on the system clipboard)

const HANDLE_CURSORS = ['nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize'];
const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const HANDLE_OFF = { nw: [-1, -1], n: [0, -1], ne: [1, -1], e: [1, 0], se: [1, 1], s: [0, 1], sw: [-1, 1], w: [-1, 0] };

// ---- one element on screen --------------------------------------------------------------
const ElementView = memo(function ElementView({ el, url, faded, hideText, shadowId, lod }) {
  if (lod && el.type === 'pen') {
    const [lcx, lcy] = center(el);
    const op = el.opacity ?? 1;
    return (
      <g transform={el.angle ? `rotate(${(el.angle * 180) / Math.PI} ${lcx} ${lcy})` : undefined} opacity={faded ? op * 0.22 : op}>
        <path d={penLodPath(el)} fill="none" stroke={el.stroke} strokeWidth={el.width * 1.1} strokeLinecap="round" strokeLinejoin="round" />
      </g>
    );
  }
  const { prims, angle, cx, cy, opacity } = primitives(el);
  return (
    <g transform={angle ? `rotate(${(angle * 180) / Math.PI} ${cx} ${cy})` : undefined} opacity={faded ? opacity * 0.22 : opacity}>
      {prims.map((p, i) => {
        if (p.k === 'path') {
          const dash = p.stroke ? dashArray(p.dash, p.width) : null;
          return (
            <path key={i} d={opsToSvg(p.ops)} fill={p.fill || 'none'} stroke={p.stroke || 'none'} strokeWidth={p.stroke ? p.width : undefined}
              strokeLinecap={p.cap} strokeLinejoin={p.join} strokeDasharray={dash ? dash.join(' ') : undefined}
              filter={p.shadow ? `url(#${shadowId})` : undefined} />
          );
        }
        if (p.k === 'text') {
          if (hideText) return null;
          return (
            <text key={i} className="notranslate" fontFamily={FAMILIES[p.font] || FAMILIES.sans} fontSize={p.size} fontWeight={p.bold ? 700 : 400}
              fontStyle={p.italic ? 'italic' : undefined} fill={p.color} xmlSpace="preserve" style={{ whiteSpace: 'pre' }}
              transform={p.rot ? `rotate(${(p.rot * 180) / Math.PI} ${p.ox} ${p.oy})` : undefined}>
              {p.lines.map((l, j) => <tspan key={j} x={l.x} y={l.y}>{l.text || ' '}</tspan>)}
            </text>
          );
        }
        if (p.k === 'image') {
          return url
            ? <image key={i} href={url} x={p.x} y={p.y} width={p.w} height={p.h} preserveAspectRatio="none" />
            : <rect key={i} x={p.x} y={p.y} width={p.w} height={p.h} fill="#e2e8f0" stroke="#cbd5e1" strokeDasharray="6 4" />;
        }
        return null;
      })}
    </g>
  );
});

const ElementsLayer = memo(function ElementsLayer({ elements, files, filesRev, erasing, hideTextId, shadowId, lod }) {
  return elements.map((el) => (
    <ElementView key={el.id} el={el} url={el.type === 'image' ? files.urlOf(el.fileId) : undefined}
      faded={erasing.has(el.id)} hideText={el.id === hideTextId} shadowId={shadowId} lod={lod} />
  ));
});

// ---- the words being typed --------------------------------------------------------------
function TextEditor({ el, view, onChange, onDone }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = ref.current; if (!t) return;
    t.focus({ preventScroll: true });
    const n = t.value.length; t.setSelectionRange(n, n);
  }, [el.id]);
  const box = textBox(el);
  const z = view.z;
  const size = el.fontSize * z;
  const lay = layoutText(el.text || ' ', { size: el.fontSize, font: el.font, bold: el.bold, italic: el.italic, maxWidth: el.type === 'text' && !el.wrap ? null : box.w });
  const h = Math.max(lay.height, el.fontSize * LINE_HEIGHT);
  const top = el.type === 'text' ? box.y : box.y + (box.h - h) / 2;
  const width = el.type === 'text' && !el.wrap ? Math.max(lay.width + el.fontSize, el.fontSize * 4) : box.w;
  let left = box.x;
  if (el.type === 'text' && !el.wrap && el.align === 'center') left = box.x + (box.w - width) / 2;
  if (el.type === 'text' && !el.wrap && el.align === 'right') left = box.x + box.w - width;
  const [cx, cy] = center(el);
  const style = {
    left: (left - view.x) * z, top: (top - view.y) * z, width: width * z + 4, height: h * z + 4,
    fontSize: size, lineHeight: LINE_HEIGHT, fontFamily: FAMILIES[el.font] || FAMILIES.sans, fontWeight: el.bold ? 700 : 400,
    fontStyle: el.italic ? 'italic' : 'normal', color: el.textColor || '#1e293b', textAlign: el.type === 'text' ? el.align : 'center',
    transformOrigin: `${(cx - left) * z}px ${(cy - top) * z}px`, transform: el.angle ? `rotate(${(el.angle * 180) / Math.PI}deg)` : undefined,
  };
  return (
    <textarea ref={ref} className="wb-text-edit" style={style} value={el.text || ''} spellCheck
      aria-label="Text" wrap={el.type === 'text' && !el.wrap ? 'off' : 'soft'}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => onDone()}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Escape' || (e.key === 'Enter' && (e.ctrlKey || e.metaKey))) { e.preventDefault(); onDone(); }
      }} />
  );
}

// ---- the board -------------------------------------------------------------------------------
export default function Board({
  model, files, session, canEdit, full, onToggleFull, fieldId, fieldName, workItem, height, onResizeHeight, memory = {}, onAttachmentAdded,
}) {
  const uid = useId().replace(/:/g, '');
  const shadowId = `wb-shadow-${uid}`;
  const rootRef = useRef(null);
  const svgRef = useRef(null);
  const layerRef = useRef(null);
  const fileInput = useRef(null);
  const zoomBtn = useRef(null);

  const subscribeModel = useCallback((fn) => model.subscribe(fn), [model]);
  const elements = useSyncExternalStore(subscribeModel, () => model.ordered());
  const subscribeFiles = useCallback((fn) => files.subscribe(fn), [files]);
  const filesRev = useSyncExternalStore(subscribeFiles, () => files.rev);
  const subscribeSession = useCallback((fn) => session.subscribe(fn), [session]);
  useSyncExternalStore(subscribeSession, () => session.state.rev);
  const live = session.state;

  const [tool, setToolState] = useState(() => memory.tool || 'select');
  const [style, setStyleState] = useState(() => ({ ...DEFAULT_STYLE, ...store.get(STYLE_KEY, {}) }));
  const [sel, setSel] = useState(() => new Set());
  const [view, setView] = useState(() => memory.view || { x: -40, y: -40, z: 1 });
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimer = useRef(null);
  const toast = useCallback((msg) => {
    setToastMsg(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(null), 6000);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  useEffect(() => { memory.view = view; memory.tool = tool; }, [memory, view, tool]);
  // where the annotation inspector was dragged to (this person, this browser)
  const [panelPos, setPanelPos] = useState(() => store.get(PANEL_KEY, null));
  const movePanel = useCallback((pos) => { setPanelPos(pos); store.set(PANEL_KEY, pos); }, []);
  const [size, setSize] = useState({ w: 1, h: 1 });   // measured on mount: nothing fits to a guess
  const [draft, setDraft] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [erasing, setErasing] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const [laser, setLaser] = useState([]);
  const [focusId, setFocusId] = useState(() => memory.focusId || null);   // the picture / PDF page in focus
  const mountFocus = useRef(memory.focusId || null);   // in focus before full screen (or back): fit it again
  const [pageBusy, setPageBusy] = useState(false);
  const [picker, setPicker] = useState(false);         // the work item's drawings
  const [zebra, setZebra] = useState(null);            // the zebra on its way there: { from, to }
  const [panelFocus, setPanelFocus] = useState(null);  // an annotation whose first box takes the cursor
  const zebraBtn = useRef(null);
  const animRef = useRef(null);
  const laf = useLookAndFeel();
  const [toolLock, setToolLock] = useState(false);
  const [exportOpen, setExportOpen] = useState(null);   // { drawing }: the export dialog, on the drawing in focus
  const [zoomOpen, setZoomOpen] = useState(false);
  const [hoverId, setHoverId] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [narrow, setNarrow] = useState(false);
  const [railCols, setRailCols] = useState(1);          // a short board folds the tool rail into columns
  const [confirmRemove, setConfirmRemove] = useState(null);   // drawings about to leave the board
  // Fingers draw until a pen is used on this device; from then on only the pen
  // draws (a hand resting on the screen is ignored) — unless the person turns
  // finger drawing back on (the hand button by the zoom).
  const [touchDraws, setTouchDrawsState] = useState(() => {
    const chosen = store.get(TOUCH_KEY, null);
    return chosen === null ? !store.get(PEN_KEY, false) : !!chosen;
  });
  const setTouchDraws = useCallback((on) => { setTouchDrawsState(on); store.set(TOUCH_KEY, on); }, []);
  const [penErasing, setPenErasing] = useState(false);   // the pen's eraser end is rubbing out

  // the latest of everything, for the event handlers
  const S = useRef({});
  S.current = { tool, style, sel, view, size, editing, canEdit, elements, toolLock, draft, focusId, narrow, railCols, touchDraws };
  const g = useRef(null);                 // the gesture in progress (pid: the pointer that drives it)
  const pointers = useRef(new Map());     // the pointers pressed on the board that it listens to
  const ignored = useRef(new Set());      // pressed but not listened to: a hand resting on the screen
  const penDown = useRef(false);          // the pen is on the screen
  const penAt = useRef(-Infinity);        // when the pen was last near the screen (hovering, touching, lifting)
  const penKnown = useRef(store.get(PEN_KEY, false));
  const lastRole = useRef('tool');        // what the last pointer pressed was (a finger's double tap writes no text)
  const spaceDown = useRef(false);
  const fitted = useRef(!!memory.view);
  const pasteCount = useRef(0);

  const setTool = useCallback((t) => {
    setToolState(t);
    if (t !== 'select' && t !== 'lasso') setSel(new Set());
    if (t === 'image') { setToolState('select'); fileInput.current?.click(); }
  }, []);
  const setStyle = useCallback((patch) => {
    setStyleState((s) => { const n = { ...s, ...patch }; store.set(STYLE_KEY, n); return n; });
  }, []);

  // ---- size, first view ---------------------------------------------------------------------
  useLayoutEffect(() => {
    const el = svgRef.current; if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize((s) => (Math.abs(s.w - r.width) < 0.5 && Math.abs(s.h - r.height) < 0.5 ? s : { w: Math.max(50, r.width), h: Math.max(50, r.height) }));
      setNarrow(r.width < 640);
    };
    measure();   // at once — a new board (full screen) fits its page to its real size from the first frame
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);
  // Every tool in view without scrolling: the rail takes one column while the
  // board is tall enough, else two, else three (each layout measured once).
  const railRef = useRef(null);
  const railFit = useRef({});
  useEffect(() => { railFit.current = {}; setRailCols(1); }, [canEdit]);
  useLayoutEffect(() => {
    const el = railRef.current; if (!el) return;
    if (narrow) { if (railCols !== 1) setRailCols(1); return; }
    railFit.current[railCols] = el.scrollHeight;
    const room = size.h - 72;
    for (const c of [1, 2, 3]) {
      const h = railFit.current[c];
      if (h === undefined) { if (c > railCols) setRailCols(c); return; }
      if (h <= room + 1 || c === 3) { if (c !== railCols) setRailCols(c); return; }
    }
  }, [size.h, narrow, railCols, canEdit]);
  const fitTo = useCallback((list, maxZ = 1) => {
    const { w, h } = S.current.size;
    const b = list && list.length ? boundsOf(list) : null;
    if (!b) { setView({ x: -w / 2, y: -h / 2, z: 1 }); return; }
    const pad = 48;
    const z = clamp(Math.min((w - pad * 2) / Math.max(1, b.w), (h - pad * 2) / Math.max(1, b.h), maxZ), MIN_Z, MAX_Z);
    setView({ x: b.x + b.w / 2 - w / 2 / z, y: b.y + b.h / 2 - h / 2 / z, z });
  }, []);
  useEffect(() => {
    if (fitted.current || size.w <= 50) return;
    fitted.current = true;
    fitTo(model.ordered(), 1);
  }, [size, fitTo, model]);

  // pictures someone else placed
  useEffect(() => { for (const el of elements) if (el.type === 'image') files.ensure(el.fileId); }, [elements, files]);

  // ---- coordinates --------------------------------------------------------------------------
  const svgRect = () => svgRef.current.getBoundingClientRect();
  const toBoard = (cx, cy) => {
    const r = svgRect(); const v = S.current.view;
    return [(cx - r.left) / v.z + v.x, (cy - r.top) / v.z + v.y];
  };
  const toScreen = (bx, by, v = view) => [(bx - v.x) * v.z, (by - v.y) * v.z];
  const zoomAt = useCallback((sx, sy, nz) => {
    setView((v) => {
      const z = clamp(nz, MIN_Z, MAX_Z);
      const bx = sx / v.z + v.x; const by = sy / v.z + v.y;
      return { x: bx - sx / z, y: by - sy / z, z };
    });
  }, []);
  const zoomBy = (f) => { const { w, h } = S.current.size; zoomAt(w / 2, h / 2, S.current.view.z * f); };

  // ---- what is on screen --------------------------------------------------------------------
  const boxCache = useRef(new WeakMap());
  const boxOf = useCallback((el) => {
    let b = boxCache.current.get(el);
    if (!b) { b = bounds(el); boxCache.current.set(el, b); }
    return b;
  }, []);
  // A big board draws only what is in view (plus a margin). While panning the
  // same set stays the same array, so nothing re-renders but the view.
  const lastVisible = useRef([]);
  const visible = useMemo(() => {
    if (elements.length < 400) return elements;
    const m = 200 / view.z;
    const vb = { x: view.x - m, y: view.y - m, w: size.w / view.z + 2 * m, h: size.h / view.z + 2 * m };
    const next = elements.filter((el) => boxesOverlap(boxOf(el), vb));
    const prev = lastVisible.current;
    if (prev.length === next.length && next.every((el, i) => prev[i] === el)) return prev;
    lastVisible.current = next;
    return next;
  }, [elements, view, size, boxOf]);
  const selected = useMemo(() => elements.filter((el) => sel.has(el.id)), [elements, sel]);

  // drop selected ids that vanished (someone deleted them)
  useEffect(() => {
    if (!sel.size) return;
    const live2 = new Set(elements.map((e) => e.id));
    let gone = false; for (const id of sel) if (!live2.has(id)) gone = true;
    if (gone && !g.current) setSel(new Set([...sel].filter((id) => live2.has(id))));
  }, [elements, sel]);

  // tell the others what I have selected
  useEffect(() => { session.sendPresence({ sel: [...sel] }); }, [sel, session]);

  const topHit = useCallback((bx, by, { includeLocked = true } = {}) => {
    const tol = 6 / S.current.view.z;
    const list = S.current.elements;
    for (let i = list.length - 1; i >= 0; i--) {
      const el = list[i];
      if (!includeLocked && el.locked) continue;
      const b = boxOf(el);
      if (bx < b.x - tol || bx > b.x + b.w + tol || by < b.y - tol || by > b.y + b.h + tol) continue;
      if (hits(el, bx, by, tol)) return el;
    }
    return null;
  }, [boxOf]);
  const groupOf = useCallback((el) => {
    if (!el.groupId) return [el.id];
    return S.current.elements.filter((e) => e.groupId === el.groupId).map((e) => e.id);
  }, []);

  // ---- the selection's frame and handles --------------------------------------------------------
  // The selection's frame: one element's own (turned) box — an annotation's
  // drawing, a dimension's lines and words — or, for several, their bounds.
  const frame = useMemo(() => {
    if (!selected.length) return null;
    if (selected.length === 1) {
      const el = selected[0];
      if ((el.type === 'line' || el.type === 'arrow') && !el.locked) return { kind: 'line', el, points: worldPoints(el) };
      let box = el.type === 'symbol' ? bodyBox(el) : localBox(el);
      if (el.type === 'pen' || el.type === 'highlighter') box = expandBox(box, el.width / 2);
      const [cx, cy] = center(el);
      return { kind: 'box', el, box, angle: el.angle || 0, cx, cy, dim: el.type === 'symbol' && el.kind === 'dimension', locked: !!el.locked && !isDrawing(el), fixed: isDrawing(el) };
    }
    const b = boundsOf(selected);
    return { kind: 'box', box: b, angle: 0, cx: b.x + b.w / 2, cy: b.y + b.h / 2, hw: b.w / 2, hh: b.h / 2, multi: true, locked: selected.every((e) => e.locked && !isDrawing(e)), fixed: selected.some(isDrawing) };
  }, [selected]);
  const frameWorld = (f, lx, ly) => (f.el ? toWorld(f.el, lx, ly) : [lx, ly]);
  const inFrame = (f, bx, by, slack) => {
    if (!f || f.kind !== 'box') return false;
    const [lx, ly] = f.el ? toLocal(f.el, bx, by) : [bx, by];
    return lx >= f.box.x - slack && lx <= f.box.x + f.box.w + slack && ly >= f.box.y - slack && ly <= f.box.y + f.box.h + slack;
  };
  const handlesOf = useCallback((f, v) => {
    if (!f || f.locked || f.fixed) return [];
    if (f.kind === 'line') return f.points.map((p, i) => ({ id: `p${i}`, index: i, at: toScreen(p[0], p[1], v), round: true }));
    const el = f.el;
    if (f.dim) {
      // a dimension: its two ends and the dimension line
      const [p0, p1] = dimEnds(el); const off = dimOffsetPoint(el);
      return [
        { id: 'd0', at: toScreen(p0[0], p0[1], v), cursor: 'crosshair', round: true },
        { id: 'd1', at: toScreen(p1[0], p1[1], v), cursor: 'crosshair', round: true },
        { id: 'doff', at: toScreen(off[0], off[1], v), cursor: 'move', diamond: true },
      ];
    }
    const pad = 6 / v.z;
    const b = expandBox(f.box, pad);
    const isSym = el && el.type === 'symbol';
    const textOnly = el && el.type === 'text';
    const out = HANDLES.filter((h) => !(textOnly && (h === 'n' || h === 's')) && !(isSym && h.length === 1)).map((h) => {
      const [ox, oy] = HANDLE_OFF[h];
      const [wx, wy] = frameWorld(f, b.x + ((ox + 1) / 2) * b.w, b.y + ((oy + 1) / 2) * b.h);
      return { id: h, at: toScreen(wx, wy, v), cursor: HANDLE_CURSORS[(HANDLES.indexOf(h) + Math.round(((f.angle * 180) / Math.PI) / 45) + 8) % 8] };
    });
    const [rx, ry] = frameWorld(f, b.x + b.w / 2, b.y - ROTATE_GAP / v.z);
    const [sx, sy] = frameWorld(f, b.x + b.w / 2, b.y);
    out.push({ id: 'rotate', at: toScreen(rx, ry, v), stem: toScreen(sx, sy, v), cursor: 'grab' });
    if (isSym && el.leader) { const le = leaderEnd(el); out.push({ id: 'leader', at: toScreen(le[0], le[1], v), cursor: 'crosshair', round: true }); }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handles = useMemo(() => (S.current.canEdit ? handlesOf(frame, view) : []), [frame, view, handlesOf, canEdit]);

  // ---- committing -----------------------------------------------------------------------------------
  const commit = useCallback((list, opts) => { if (list.length) model.commit(list, opts); }, [model]);
  const topZ = () => model.maxZ() + 1;
  const afterCreate = (ids) => {
    if (!S.current.toolLock) { setToolState('select'); setSel(new Set(ids)); }
  };

  // ---- text editing -----------------------------------------------------------------------------------
  const startEditing = useCallback((el, isNew = false) => {
    if (!S.current.canEdit || el.locked || !HAS_TEXT.has(el.type)) return;
    model.setPreview(new Map([[el.id, el]]));
    setEditing({ id: el.id, isNew });
    setSel(new Set([el.id]));
  }, [model]);
  const editText = useCallback((text) => {
    const ed = S.current.editing; if (!ed) return;
    const el = model.get(ed.id); if (!el) return;
    let next = { ...el, text };
    next = next.type === 'text' ? fitText(next) : next.type === 'note' ? fitNote(next) : next;
    model.setPreview(new Map([[el.id, next]]));
  }, [model]);
  const finishEditing = useCallback(() => {
    const ed = S.current.editing; if (!ed) return;
    setEditing(null);
    const el = model.get(ed.id);
    if (!el) { model.clearPreview(); return; }
    const empty = !String(el.text || '').trim();
    if (empty && el.type === 'text') {
      model.clearPreview();
      if (model.els.has(el.id)) commit([{ id: el.id, deleted: true }]);
      setSel(new Set());
      return;
    }
    const before = model.els.get(el.id);
    if (before && before.text === el.text && before.h === el.h && before.w === el.w) { model.clearPreview(); return; }
    commit([el]);
  }, [model, commit]);

  // ---- pictures ----------------------------------------------------------------------------------------
  const placeFiles = useCallback(async (fileList, at) => {
    if (!S.current.canEdit) return;
    const pdfs = [...fileList].filter((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name || ''));
    for (const pdf of pdfs) {
      toast?.(`Reading ${pdf.name}…`);
      try {
        if (pdf.size > 60 * 1024 * 1024) throw new Error('PDFs may be at most 60 MB.');
        const res = await whiteboardApi.upload(workItem.key, fieldId, { id: newId(), blob: pdf, name: pdf.name });
        placeFromServerRef.current?.({ kind: 'pdf', pdf: res.pdf, page: res.page }, pdf.name);
        toast?.(null);
      } catch (e) {
        toast?.(e?.response?.data?.error || e?.message || 'That PDF could not be read.');
      }
    }
    const pics = [...fileList].filter((f) => /^image\//.test(f.type));
    if (!pics.length) { if (!pdfs.length) toast?.('Only pictures and PDFs can be placed on the whiteboard.'); return; }
    const v = S.current.view; const { w, h } = S.current.size;
    let [px, py] = at || [v.x + w / 2 / v.z, v.y + h / 2 / v.z];
    const made = [];
    for (const file of pics) {
      const id = newId();
      const elId = newId();
      try {
        const placed = await files.place(file, {
          id, key: workItem.key, fieldId,
          onFailed: (msg) => {
            toast?.(`The picture could not be uploaded: ${msg}`);
            if (model.els.get(elId)) model.commit([{ id: elId, deleted: true }], { history: false });
          },
        });
        const maxSide = Math.min(w, h) * 0.6 / v.z;
        const k = Math.min(1 / v.z, maxSide / Math.max(placed.w, placed.h));
        const ew = Math.max(8, placed.w * k); const eh = Math.max(8, placed.h * k);
        const el = makeElement('image', { id: elId, fileId: id, x: px - ew / 2, y: py - eh / 2, w: ew, h: eh, z: topZ() });
        commit([el]);
        made.push(el.id);
        px += 24 / v.z; py += 24 / v.z;
      } catch (e) {
        toast?.(e?.message || 'The picture could not be placed.');
      }
    }
    if (made.length) { setToolState('select'); setSel(new Set(made)); }
  }, [files, model, commit, toast, workItem, fieldId]);

  // ---- focus: one picture / PDF page fills the view, ready to mark up -------------------------------
  const animateTo = useCallback((target) => {
    cancelAnimationFrame(animRef.current);
    const from = S.current.view; const t0 = performance.now(); const dur = 340;
    const step = (now) => {
      const t = Math.min(1, (now - t0) / dur); const k = 1 - (1 - t) ** 3;
      const z = from.z * (target.z / from.z) ** k;
      setView({ x: from.x + (target.x - from.x) * k, y: from.y + (target.y - from.y) * k, z });
      if (t < 1) animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
  }, []);
  useEffect(() => () => cancelAnimationFrame(animRef.current), []);
  const viewFor = useCallback((b) => {
    const { w, h } = S.current.size;
    const top = 60; const bottom = 76; const side = S.current.narrow ? 16 : 64 + (S.current.railCols - 1) * 33;
    const z = clamp(Math.min((w - side - 24) / Math.max(1, b.w), (h - top - bottom) / Math.max(1, b.h)), MIN_Z, MAX_Z);
    return { x: b.x + b.w / 2 - (side + (w - side - 24) / 2) / z, y: b.y + b.h / 2 - (top + (h - top - bottom) / 2) / z, z };
  }, []);
  const focusOn = useCallback((el) => {
    setFocusId(el.id);
    setSel(new Set());
    setToolState((t) => (t === 'select' || t === 'lasso' || t === 'hand' ? 'pen' : t));
    animateTo(viewFor(bounds(el)));
  }, [animateTo, viewFor]);
  const exitFocus = useCallback(() => setFocusId(null), []);
  useEffect(() => { memory.focusId = focusId; }, [memory, focusId]);
  // going to full screen (or back) opens a new board of a new size: the page in
  // focus stays in focus, fitted to the new size
  useEffect(() => {
    if (!mountFocus.current || size.w <= 50) return;
    const el = model.get(mountFocus.current);
    mountFocus.current = null;
    if (el) setView(viewFor(bounds(el)));
  }, [size, model, viewFor]);
  // the page in focus was deleted (here or by someone else): leave focus
  useEffect(() => { if (focusId && !elements.some((x) => x.id === focusId)) setFocusId(null); }, [elements, focusId]);

  // A picture or PDF page the server made: on the board (behind the ink),
  // beside what is there, in focus. Drawing on it never moves it: a drawing
  // moves only once it is selected (and then its markup moves with it).
  const placeFromServer = useCallback((res, name) => {
    const list = model.ordered();   // what is placed a moment ago counts (several in a row)
    let el;
    if (res.kind === 'pdf' || res.pdf) {
      const pg = res.page; files.ensure(pg.file.id);
      const w = pg.pt ? (pg.pt.w * 96) / 72 : (pg.file.width || 1600) * 0.48;
      const h = pg.pt ? (pg.pt.h * 96) / 72 : (pg.file.height || 1100) * 0.48;
      el = makeElement('image', { fileId: pg.file.id, doc: { fileId: res.pdf.id, page: pg.page || 1, pages: res.pdf.pages || pg.pages || 1, name: res.pdf.name || name || 'PDF' }, w, h });
    } else {
      files.ensure(res.file.id);
      const k = Math.min(1, 2400 / Math.max(res.file.width || 1, res.file.height || 1));
      el = makeElement('image', { fileId: res.file.id, doc: { fileId: res.file.id, page: 1, pages: 1, name: res.name || name || 'Picture' }, w: (res.file.width || 1200) * k, h: (res.file.height || 800) * k });
    }
    const all = boundsOf(list);
    const v = S.current.view; const { w: W, h: H } = S.current.size;
    const x = all ? all.x + all.w + 120 : v.x + W / 2 / v.z - el.w / 2;
    const y = all ? all.y : v.y + H / 2 / v.z - el.h / 2;
    el = { ...el, x, y, z: model.minZ() - 1 };
    commit([el]);
    focusOn(el);
  }, [files, model, commit, focusOn]);
  const placeFromServerRef = useRef(null);
  placeFromServerRef.current = placeFromServer;

  // Another page of the PDF in focus: the one already on the board, or rendered
  // now and laid beside it.
  const gotoPage = useCallback(async (n) => {
    const cur = S.current.elements.find((x) => x.id === S.current.focusId);
    const d = cur?.doc; if (!d || n < 1 || n > d.pages) return;
    const there = S.current.elements.find((x) => x.doc && x.doc.fileId === d.fileId && x.doc.page === n);
    if (there) { focusOn(there); return; }
    setPageBusy(true);
    try {
      const res = await whiteboardApi.pdfPage(workItem.key, fieldId, d.fileId, n);
      files.ensure(res.file.id);
      const w = res.pt ? (res.pt.w * 96) / 72 : (res.file.width || 1600) * 0.48;
      const h = res.pt ? (res.pt.h * 96) / 72 : (res.file.height || 1100) * 0.48;
      const x = n > d.page ? cur.x + cur.w + PAGE_GAP : cur.x - w - PAGE_GAP;
      const el = makeElement('image', { fileId: res.file.id, doc: { ...d, page: res.page || n, pages: res.pages || d.pages }, x, y: cur.y, w, h, z: model.minZ() - 1 });
      commit([el]);
      focusOn(el);
    } catch (e) {
      toast(e?.response?.data?.error || e?.message || 'That page could not be read.');
    } finally { setPageBusy(false); }
  }, [focusOn, files, model, commit, toast, workItem, fieldId]);

  // The shop zebra runs from its button to the drawings pop-up.
  const openDrawings = useCallback(() => {
    if (!S.current.canEdit) return;
    const root = rootRef.current?.getBoundingClientRect(); const btn = zebraBtn.current?.getBoundingClientRect();
    if (!root || !btn) { setPicker(true); return; }
    const W = root.width; const H = root.height;
    const modalW = Math.min(760, W - 32); const top = Math.round(clamp(H * 0.13, 74, 150));
    const from = { x: btn.left - root.left + btn.width / 2, y: btn.top - root.top + btn.height };
    const to = { x: (W - modalW) / 2 + 30 + 55, y: top + 3 };
    setZebra({ from, to, top });
  }, []);

  // ---- pointer gestures ---------------------------------------------------------------------------------
  // Sizes are the board's own (like paper): a Bold pen is equally bold for
  // everyone, at whatever zoom it was drawn or is looked at.
  const styleFor = (type) => {
    const st = S.current.style;
    const out = { stroke: st.stroke, fill: st.fill, dash: st.dash, fontSize: st.fontSize, font: st.font, align: st.align, bold: st.bold, noteColor: st.noteColor, highlight: st.highlight, startHead: st.startHead, endHead: st.endHead };
    if (type === 'pen') out.width = st.penWidth;
    else if (type === 'highlighter') out.highlightWidth = st.highlightWidth;
    else out.width = st.width;
    return out;
  };

  const touchesDown = () => [...pointers.current.values()].filter((p) => p.type === 'touch');
  function startPinch() {
    const pts = touchesDown();
    const r = svgRect();
    const mid = [((pts[0].x + pts[1].x) / 2) - r.left, ((pts[0].y + pts[1].y) / 2) - r.top];
    g.current = { kind: 'pinch', d0: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1, mid0: mid, v0: S.current.view };
  }
  function cancelGesture() {
    const cur = g.current; g.current = null;
    if (!cur) return;
    if (['move', 'resize', 'rotate', 'point', 'leader', 'dimend', 'dimoff'].includes(cur.kind)) model.clearPreview();
    setDraft(null); setOverlay(null); setErasing(new Set()); setPenErasing(false);
    session.sendPresence({ drafts: [], erasing: [] });
  }

  // The pen near the screen (hovering, touching, lifting — on the board or its
  // bars): a touch now is the hand that holds it. The first pen on a device
  // makes it pen-only, unless the person chose otherwise.
  const notePen = (e) => {
    if (e.pointerType !== 'pen') return;
    penAt.current = performance.now();
    if (e.type === 'pointermove' && !e.buttons) penDown.current = false;   // hovering: it is off the screen (a lift the board missed)
    if (penKnown.current) return;
    penKnown.current = true;
    store.set(PEN_KEY, true);
    if (store.get(TOUCH_KEY, null) === null && S.current.touchDraws) {
      setTouchDrawsState(false);
      toast('Pen found: from now on only the pen draws — a hand resting on the screen is ignored, two fingers move and zoom, the pen\'s eraser end rubs out.');
    }
  };

  function onPointerDown(e) {
    if (e.button === 2) return;
    const st = S.current;
    notePen(e);
    const role = pointerRole(e, { touchDraws: st.touchDraws, penDown: penDown.current, penAt: penAt.current, now: performance.now() });
    lastRole.current = role;
    if (role === 'ignore') { ignored.current.add(e.pointerId); return; }
    if (e.pointerType === 'pen') {
      penDown.current = true;
      // the pen wins: what fingers were doing stops, and they are not heard again until lifted
      for (const [id, p] of pointers.current) if (p.type === 'touch') { pointers.current.delete(id); ignored.current.add(id); }
      if (g.current && (g.current.ptype === 'touch' || g.current.kind === 'pinch')) cancelGesture();
    }
    if (st.editing) finishEditing();
    rootRef.current?.focus({ preventScroll: true });
    try { svgRef.current.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY, type: e.pointerType, role });
    if (e.pointerType === 'touch') {
      const n = touchesDown().length;
      if (n === 2) { cancelGesture(); startPinch(); return; }
      if (n > 2) return;
    }
    startGesture(e, role, st);
    if (g.current && g.current.pid == null && g.current.kind !== 'pinch') { g.current.pid = e.pointerId; g.current.ptype = e.pointerType; }
  }

  function startGesture(e, role, st) {
    const [bx, by] = toBoard(e.clientX, e.clientY);
    if (role === 'eraser') {
      // the pen turned round: rub out, whatever tool is picked — turned back, the pen has its tool again
      if (!st.canEdit) return undefined;
      g.current = { kind: 'erase', last: [bx, by], hit: new Set(), eraserEnd: true };
      setPenErasing(true);
      eraseTo(bx, by);
      return undefined;
    }
    if (role === 'finger') {
      // only the pen draws: one finger alone does nothing (two move and zoom) — with Move the view picked, it moves the view
      if (st.tool === 'hand') { g.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, v0: st.view }; setCursor('grabbing'); }
      return undefined;
    }
    if (e.button === 1 || spaceDown.current || st.tool === 'hand') {
      g.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, v0: st.view };
      setCursor('grabbing');
      return undefined;
    }
    const tool = st.canEdit ? st.tool : (['select', 'hand', 'laser', 'lasso'].includes(st.tool) ? st.tool : 'select');
    if (tool === 'select') return downSelect(e, bx, by);
    if (tool === 'lasso') {
      // what the lasso caught moves at once: press inside it (or on a handle) and drag
      if (st.sel.size && (hitHandle(e) || inFrame(frame, bx, by, 4 / st.view.z))) return downSelect(e, bx, by);
      g.current = { kind: 'lasso', pts: [[bx, by]], base: e.shiftKey ? new Set(st.sel) : new Set() }; setOverlay({ lasso: [[bx, by]] }); return;
    }
    if (tool === 'symbol') {
      if ((st.style.symbol || 'fcf') === 'dimension') return startShape(e, bx, by, 'dimension');
      g.current = { kind: 'place', what: 'symbol', sx: e.clientX, sy: e.clientY, bx, by }; return;
    }
    if (tool === 'pen' || tool === 'highlighter') return startDraw(e, bx, by, tool);
    if (tool === 'eraser') {
      g.current = { kind: 'erase', last: [bx, by], hit: new Set() };
      eraseTo(bx, by);
      return;
    }
    if (tool === 'laser') { g.current = { kind: 'laser' }; addLaser(bx, by); return; }
    if (tool === 'shape' || tool === 'line' || tool === 'arrow') return startShape(e, bx, by, tool === 'shape' ? st.style.shape : tool);
    if (tool === 'text' || tool === 'note') { g.current = { kind: 'place', what: tool, sx: e.clientX, sy: e.clientY, bx, by }; return; }
    return undefined;
  }

  function hitHandle(e) {
    const r = svgRect();
    const sx = e.clientX - r.left; const sy = e.clientY - r.top;
    for (const h of handles) if (Math.hypot(h.at[0] - sx, h.at[1] - sy) <= HANDLE_PX) return h;
    return null;
  }

  function downSelect(e, bx, by) {
    const st = S.current;
    const h = st.canEdit ? hitHandle(e) : null;
    if (h && frame) {
      const orig = new Map(selected.filter((el) => !el.locked).map((el) => [el.id, el]));
      if (h.id === 'rotate') {
        g.current = { kind: 'rotate', orig, cx: frame.cx, cy: frame.cy, a0: Math.atan2(by - frame.cy, bx - frame.cx), single: selected.length === 1 ? selected[0] : null };
      } else if (h.id === 'leader') {
        g.current = { kind: 'leader', id: frame.el.id, orig: frame.el };
      } else if (h.id === 'd0' || h.id === 'd1') {
        g.current = { kind: 'dimend', id: frame.el.id, which: h.id === 'd0' ? 0 : 1, orig: frame.el };
      } else if (h.id === 'doff') {
        g.current = { kind: 'dimoff', id: frame.el.id, orig: frame.el };
      } else if (frame.kind === 'line') {
        g.current = { kind: 'point', id: frame.el.id, index: h.index, orig: frame.el };
      } else {
        g.current = { kind: 'resize', handle: h.id, orig, frame, single: selected.length === 1 ? selected[0] : null };
      }
      return;
    }
    const hit = topHit(bx, by);
    // inside the selection's frame: a drag moves the selection (whatever is under the pointer)
    if (st.canEdit && !e.shiftKey && frame && !frame.locked && inFrame(frame, bx, by, 4 / st.view.z) && (!hit || st.sel.has(hit.id) || hit.locked)) {
      g.current = { kind: 'press', sx: e.clientX, sy: e.clientY, bx, by, hit: hit && st.sel.has(hit.id) ? hit : null, toggleOff: false, sel: st.sel, alt: e.altKey, inFrame: true };
      return;
    }
    // a drawing (or anything locked) is the paper: a drag on it draws a selection
    // box; a click selects it — and once selected, a drawing drags with its markup
    if (hit && (hit.locked || isDrawing(hit)) && !st.sel.has(hit.id)) {
      g.current = { kind: 'press-empty', sx: e.clientX, sy: e.clientY, bx, by, shift: e.shiftKey, base: e.shiftKey ? new Set(st.sel) : new Set(), lockedHit: hit };
      return;
    }
    if (hit) {
      const ids = groupOf(hit);
      const was = st.sel.has(hit.id);
      let next = new Set(st.sel);
      if (e.shiftKey) { if (!was) ids.forEach((id) => next.add(id)); } else if (!was) next = new Set(ids);
      setSel(next);
      g.current = { kind: 'press', sx: e.clientX, sy: e.clientY, bx, by, hit, toggleOff: e.shiftKey && was, sel: next, alt: e.altKey };
      return;
    }
    g.current = { kind: 'press-empty', sx: e.clientX, sy: e.clientY, bx, by, shift: e.shiftKey, base: e.shiftKey ? new Set(st.sel) : new Set() };
  }

  function startDraw(e, bx, by, type) {
    const isPen = e.pointerType === 'pen';
    const p = isPen ? (e.pressure || 0.5) : 0.5;
    const el = makeElement(type, { x: 0, y: 0, points: [[bx, by, p]], pen: isPen && type === 'pen' ? true : undefined }, styleFor(type));
    if (!(isPen && type === 'pen')) delete el.pen;
    g.current = { kind: 'draw', el, pts: [[bx, by, p]], lastT: e.timeStamp, last: [e.clientX, e.clientY], pressure: p, isPen };
    setDraft(el);
  }
  function startShape(e, bx, by, type) {
    const el = type === 'dimension'
      ? makeElement('symbol', { kind: 'dimension', x: bx, y: by, w: 0, h: 0 }, { stroke: S.current.style.stroke, symbolSize: S.current.style.symbolSize })
      : makeElement(type, type === 'line' || type === 'arrow' ? { x: 0, y: 0, points: [[bx, by], [bx, by]] } : { x: bx, y: by, w: 0, h: 0 }, styleFor(type));
    g.current = { kind: 'shape', type, bx, by, sx: e.clientX, sy: e.clientY, el };
    setDraft(el);
  }

  function eraseTo(bx, by) {
    const cur = g.current; if (!cur) return;
    const r = 8 / S.current.view.z;
    const a = cur.last; const b = [bx, by];
    const seg = { x: Math.min(a[0], b[0]) - r, y: Math.min(a[1], b[1]) - r, w: Math.abs(a[0] - b[0]) + 2 * r, h: Math.abs(a[1] - b[1]) + 2 * r };
    let added = false;
    for (const el of S.current.elements) {
      if (el.locked || isDrawing(el) || cur.hit.has(el.id)) continue;
      if (!boxesOverlap(boxOf(el), seg)) continue;
      if (touchesSegment(el, a, b, r)) { cur.hit.add(el.id); added = true; }
    }
    cur.last = b;
    const trail = [...(overlay?.eraser || []), [bx, by]].slice(-14);
    setOverlay({ eraser: trail });
    if (added) { setErasing(new Set(cur.hit)); session.sendPresence({ erasing: [...cur.hit] }); }
  }

  function addLaser(bx, by) {
    const now = performance.now();
    setLaser((l) => {
      const next = [...l.filter((q) => now - q.t < 900), { x: bx, y: by, t: now }];
      session.sendPresence({ laser: next.map((q) => [q.x, q.y]) });
      return next;
    });
  }
  // the laser trail fades on its own
  useEffect(() => {
    if (!laser.length) return undefined;
    const id = setTimeout(() => {
      const now = performance.now();
      setLaser((l) => {
        const next = l.filter((q) => now - q.t < 900);
        if (next.length !== l.length) session.sendPresence({ laser: next.map((q) => [q.x, q.y]) });
        return next;
      });
    }, 60);
    return () => clearTimeout(id);
  }, [laser, session]);

  function onPointerMove(e) {
    if (ignored.current.has(e.pointerId)) return;
    const p = pointers.current.get(e.pointerId);
    if (p) { p.x = e.clientX; p.y = e.clientY; }
    const cur = g.current;
    // a gesture follows its own pointer: not a pen hovering beside a finger's stroke, not a second finger
    if (cur && cur.kind !== 'pinch' && (!p || (cur.pid != null && cur.pid !== e.pointerId))) return;
    if (!cur && p?.role === 'finger') return;
    const [bx, by] = toBoard(e.clientX, e.clientY);
    if (!cur || cur.kind === 'laser') session.sendPresence({ x: bx, y: by, tool: S.current.tool, away: false });
    if (!cur) { hover(e, bx, by); return; }
    const st = S.current;
    switch (cur.kind) {
      case 'pan':
        setView({ ...cur.v0, x: cur.v0.x - (e.clientX - cur.sx) / cur.v0.z, y: cur.v0.y - (e.clientY - cur.sy) / cur.v0.z });
        return;
      case 'pinch': {
        const pts = [...pointers.current.values()];
        if (pts.length < 2) return;
        const r = svgRect();
        const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
        const mid = [((pts[0].x + pts[1].x) / 2) - r.left, ((pts[0].y + pts[1].y) / 2) - r.top];
        const v0 = cur.v0;
        const z = clamp(v0.z * (d / cur.d0), MIN_Z, MAX_Z);
        const b0 = [cur.mid0[0] / v0.z + v0.x, cur.mid0[1] / v0.z + v0.y];
        setView({ x: b0[0] - mid[0] / z, y: b0[1] - mid[1] / z, z });
        return;
      }
      case 'draw': {
        const evs = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
        const list = evs.length ? evs : [e];
        for (const ev of list) {
          const [x, y] = toBoard(ev.clientX, ev.clientY);
          const lastPt = cur.pts[cur.pts.length - 1];
          if (Math.hypot(x - lastPt[0], y - lastPt[1]) < 0.6 / st.view.z) continue;
          let pr;
          if (cur.isPen) pr = ev.pressure || cur.pressure;
          else {
            const dt = Math.max(1, ev.timeStamp - cur.lastT);
            const speed = Math.hypot(ev.clientX - cur.last[0], ev.clientY - cur.last[1]) / dt;
            pr = simulatedPressure(cur.pressure, speed);
          }
          cur.pressure = pr; cur.lastT = ev.timeStamp; cur.last = [ev.clientX, ev.clientY];
          cur.pts.push([x, y, Math.round(pr * 1000) / 1000]);
        }
        const next = { ...cur.el, points: cur.pts.slice() };
        setDraft(next);
        if (cur.pts.length % 3 === 0) session.sendPresence({ drafts: [{ ...next, points: next.points.slice(-1500) }] });
        return;
      }
      case 'shape': {
        let next;
        if (cur.type === 'dimension') {
          let ex = bx; let ey = by;
          // a dimension snaps to 15° steps (drawings measure square to the view); Alt frees it
          if (!e.altKey) {
            const a = Math.round(Math.atan2(by - cur.by, bx - cur.bx) / (Math.PI / 12)) * (Math.PI / 12);
            const len = Math.hypot(bx - cur.bx, by - cur.by);
            ex = cur.bx + Math.cos(a) * len; ey = cur.by + Math.sin(a) * len;
          }
          next = dimMoveEnd({ ...cur.el, x: cur.bx, y: cur.by, w: 0, angle: 0 }, 1, ex, ey);
        } else if (cur.type === 'line' || cur.type === 'arrow') {
          let ex = bx; let ey = by;
          if (e.shiftKey) {
            const a = Math.round(Math.atan2(by - cur.by, bx - cur.bx) / (Math.PI / 12)) * (Math.PI / 12);
            const len = Math.hypot(bx - cur.bx, by - cur.by);
            ex = cur.bx + Math.cos(a) * len; ey = cur.by + Math.sin(a) * len;
          }
          next = { ...cur.el, points: [[cur.bx, cur.by], [ex, ey]] };
        } else {
          let w = bx - cur.bx; let h = by - cur.by;
          if (e.shiftKey) { const m = Math.max(Math.abs(w), Math.abs(h)); w = Math.sign(w || 1) * m; h = Math.sign(h || 1) * m; }
          let x = w < 0 ? cur.bx + w : cur.bx; let y = h < 0 ? cur.by + h : cur.by;
          if (e.altKey) { x = cur.bx - Math.abs(w); y = cur.by - Math.abs(h); w *= 2; h *= 2; }
          next = { ...cur.el, x, y, w: Math.abs(w), h: Math.abs(h) };
        }
        cur.el = next;
        setDraft(next);
        session.sendPresence({ drafts: [next] });
        return;
      }
      case 'press': {
        if (Math.hypot(e.clientX - cur.sx, e.clientY - cur.sy) < DRAG_PX) return;
        if (!st.canEdit) { g.current = null; return; }
        const movable = S.current.elements.filter((el) => cur.sel.has(el.id) && (!el.locked || isDrawing(el)));
        if (!movable.length) {
          // only locked things selected: the drag draws a selection box instead
          g.current = { kind: 'marquee', start: [cur.bx, cur.by], base: new Set(), pid: cur.pid, ptype: cur.ptype };
          return onPointerMove(e);
        }
        let orig = new Map(movable.map((el) => [el.id, el]));
        // a drawing takes its markup along
        if (!cur.alt) for (const r of ridersOf(S.current.elements, movable.filter(isDrawing))) if (!orig.has(r.id)) orig.set(r.id, r);
        let dupIds = null;
        if (cur.alt) {
          // Alt-drag leaves the originals and moves copies
          const z0 = topZ(); const gids = new Map();
          const copies = movable.map((el, i) => {
            let gid = el.groupId;
            if (gid) { if (!gids.has(gid)) gids.set(gid, newId()); gid = gids.get(gid); }
            return { ...el, id: newId(), z: z0 + i, version: 0, groupId: gid, locked: undefined };
          });
          orig = new Map(copies.map((c) => [c.id, c]));
          dupIds = copies.map((c) => c.id);
          setSel(new Set(dupIds));
        }
        g.current = { kind: 'move', orig, start: [cur.bx, cur.by], dup: !!dupIds, pid: cur.pid, ptype: cur.ptype };
        setCursor('move');
        return onPointerMove(e);
      }
      case 'move': {
        let dx = bx - cur.start[0]; let dy = by - cur.start[1];
        if (e.shiftKey) { if (Math.abs(dx) > Math.abs(dy)) dy = 0; else dx = 0; }
        const pv = new Map();
        for (const [id, el] of cur.orig) pv.set(id, moveBy(el, dx, dy));
        cur.moved = dx !== 0 || dy !== 0;
        model.setPreview(pv);
        return;
      }
      case 'press-empty': {
        if (Math.hypot(e.clientX - cur.sx, e.clientY - cur.sy) < DRAG_PX) return;
        g.current = { kind: 'marquee', start: [cur.bx, cur.by], base: cur.base, pid: cur.pid, ptype: cur.ptype };
        return onPointerMove(e);
      }
      case 'marquee': {
        const box = { x: Math.min(cur.start[0], bx), y: Math.min(cur.start[1], by), w: Math.abs(bx - cur.start[0]), h: Math.abs(by - cur.start[1]) };
        setOverlay({ marquee: box });
        const next = new Set(cur.base);
        for (const el of st.elements) if (boxContains(box, boxOf(el))) groupOf(el).forEach((id) => next.add(id));
        setSel(next);
        return;
      }
      case 'lasso': {
        const last = cur.pts[cur.pts.length - 1];
        if (Math.hypot(bx - last[0], by - last[1]) < 3 / st.view.z) return;
        cur.pts.push([bx, by]);
        setOverlay({ lasso: cur.pts.slice() });
        if (cur.pts.length > 3) {
          const next = new Set(cur.base);
          for (const el of st.elements) if (insideLasso(el, cur.pts)) groupOf(el).forEach((id) => next.add(id));
          setSel(next);
        }
        return;
      }
      case 'resize': {
        const pv = new Map();
        if (cur.single) {
          const keep = cur.single.type === 'image' ? !e.shiftKey : e.shiftKey;
          pv.set(cur.single.id, resizeOne(cur.single, cur.handle, bx, by, { keepRatio: keep, fromCenter: e.altKey }));
        } else {
          const f = cur.frame;
          const [ox, oy] = HANDLE_OFF[cur.handle];
          const ax = f.cx - ox * f.hw; const ay = f.cy - oy * f.hh;
          const hx = f.cx + ox * f.hw; const hy = f.cy + oy * f.hh;
          let sx = ox ? (bx - ax) / ((hx - ax) || 1) : 1;
          let sy = oy ? (by - ay) / ((hy - ay) || 1) : 1;
          if (e.shiftKey || (ox && oy)) {
            const s = ox && oy ? Math.max(Math.abs(sx), Math.abs(sy)) : (ox ? Math.abs(sx) : Math.abs(sy));
            if (e.shiftKey || (ox && oy)) { sx = Math.sign(sx || 1) * s; sy = Math.sign(sy || 1) * s; }
          }
          if (Math.abs(sx) < 0.02) sx = 0.02 * Math.sign(sx || 1);
          if (Math.abs(sy) < 0.02) sy = 0.02 * Math.sign(sy || 1);
          for (const [id, el] of cur.orig) pv.set(id, scaleWithin(el, ax, ay, sx, sy));
        }
        model.setPreview(pv);
        return;
      }
      case 'rotate': {
        let da = Math.atan2(by - cur.cy, bx - cur.cx) - cur.a0;
        const step = Math.PI / 12;
        if (e.shiftKey) {
          if (cur.single) da = Math.round(((cur.single.angle || 0) + da) / step) * step - (cur.single.angle || 0);
          else da = Math.round(da / step) * step;
        }
        const pv = new Map();
        for (const [id, el] of cur.orig) pv.set(id, rotateAbout(el, cur.cx, cur.cy, da));
        model.setPreview(pv);
        return;
      }
      case 'point': {
        let px = bx; let py = by;
        if (e.shiftKey) {
          const pts = worldPoints(cur.orig);
          const other = pts[cur.index === 0 ? 1 : pts.length - 2] || pts[0];
          const a = Math.round(Math.atan2(by - other[1], bx - other[0]) / (Math.PI / 12)) * (Math.PI / 12);
          const len = Math.hypot(bx - other[0], by - other[1]);
          px = other[0] + Math.cos(a) * len; py = other[1] + Math.sin(a) * len;
        }
        model.setPreview(new Map([[cur.id, movePoint(cur.orig, cur.index, px, py)]]));
        return;
      }
      case 'leader':
        model.setPreview(new Map([[cur.id, moveLeader(cur.orig, bx, by)]]));
        return;
      case 'dimend': {
        let px = bx; let py = by;
        if (!e.altKey) {
          const other = dimEnds(cur.orig)[cur.which ? 0 : 1];
          const a = Math.round(Math.atan2(by - other[1], bx - other[0]) / (Math.PI / 12)) * (Math.PI / 12);
          const len = Math.hypot(bx - other[0], by - other[1]);
          px = other[0] + Math.cos(a) * len; py = other[1] + Math.sin(a) * len;
        }
        model.setPreview(new Map([[cur.id, dimMoveEnd(cur.orig, cur.which, px, py)]]));
        return;
      }
      case 'dimoff':
        model.setPreview(new Map([[cur.id, dimSetOffset(cur.orig, bx, by)]]));
        return;
      case 'erase': eraseTo(bx, by); return;
      case 'laser': addLaser(bx, by); return;
      default: return;
    }
  }

  function hover(e, bx, by) {
    const st = S.current;
    if (spaceDown.current || st.tool === 'hand') { setCursor('grab'); return; }
    if (st.tool !== 'select') { setHoverId(null); setCursor(null); return; }
    const h = st.canEdit ? hitHandle(e) : null;
    if (h) { setCursor(h.cursor || 'pointer'); setHoverId(null); return; }
    const hit = topHit(bx, by);
    setHoverId(hit && !st.sel.has(hit.id) ? hit.id : null);
    setCursor(hit ? (hit.locked || !st.canEdit || (isDrawing(hit) && !st.sel.has(hit.id)) ? 'default' : 'move') : null);
  }

  function onPointerUp(e) {
    if (e.pointerType === 'pen') { penDown.current = false; penAt.current = performance.now(); }
    if (ignored.current.delete(e.pointerId)) return;
    const had = pointers.current.delete(e.pointerId);
    const cur = g.current;
    if (!cur || !had) return;
    if (cur.kind === 'pinch') { g.current = null; return; }
    // another pointer lifting (a finger beside a stroke) leaves the gesture going
    if (cur.pid != null ? cur.pid !== e.pointerId : pointers.current.size > 0 && cur.kind !== 'pan') return;
    g.current = null;
    const st = S.current;
    const [bx, by] = toBoard(e.clientX, e.clientY);
    const cancelled = e.type === 'pointercancel';
    switch (cur.kind) {
      case 'pan': setCursor(spaceDown.current || st.tool === 'hand' ? 'grab' : null); return;
      case 'draw': {
        setDraft(null);
        session.sendPresence({ drafts: [] });
        if (cancelled && cur.pts.length < 3) return;
        // drop the points that change nothing (each kept point keeps its pressure)
        let pts = cur.pts.length > 2 ? simplify(cur.pts, 0.35 / st.view.z) : cur.pts;
        if (pts.length > 15000) pts = pts.filter((_, i) => i % Math.ceil(pts.length / 15000) === 0);
        const el = normalizeLinear({ ...cur.el, points: pts.map((p) => [p[0], p[1], p[2] ?? 0.5]), z: topZ() });
        commit([el]);
        return;
      }
      case 'shape': {
        setDraft(null);
        session.sendPresence({ drafts: [] });
        if (cancelled) return;
        const small = Math.hypot(e.clientX - cur.sx, e.clientY - cur.sy) < DRAG_PX;
        let el = cur.el;
        if (cur.type === 'dimension') {
          if (small || el.w < 4) return;
          el = { ...el, z: topZ() };
          commit([el]);
          if (!st.toolLock) setToolState('select');
          setSel(new Set([el.id])); setPanelFocus(el.id);
          return;
        }
        if (cur.type === 'line' || cur.type === 'arrow') {
          if (small) return;
          el = normalizeLinear(el);
        } else if (small) {
          const w = 160; const h = cur.type === 'rect' ? 100 : 120;
          el = { ...el, x: cur.bx - w / 2, y: cur.by - h / 2, w, h };
        }
        el = { ...el, z: topZ() };
        commit([el]);
        afterCreate([el.id]);
        return;
      }
      case 'place': {
        if (cancelled || Math.hypot(e.clientX - cur.sx, e.clientY - cur.sy) > 8) return;
        const sty = styleFor(cur.what);
        let el;
        if (cur.what === 'symbol') {
          // the click is where it points: the leader's end, the surface, the centre
          const k = st.style.symbol || 'fcf';
          el = makeElement('symbol', { kind: k, x: 0, y: 0, z: topZ() }, { stroke: st.style.stroke, symbolSize: st.style.symbolSize });
          if (el.leader) el = { ...el, x: cur.bx - el.leader[0], y: cur.by - el.leader[1] };
          else if (k === 'finish') { const a = layoutSymbol(el).anchor; el = { ...el, x: cur.bx - a[0], y: cur.by - a[1] }; } else el = { ...el, x: cur.bx - el.w / 2, y: cur.by - el.h / 2 };
          commit([el]);
          if (!st.toolLock) setToolState('select');
          setSel(new Set([el.id])); setPanelFocus(el.id);
          return;
        }
        if (cur.what === 'text') {
          el = fitText(makeElement('text', { x: cur.bx, y: cur.by - (sty.fontSize * LINE_HEIGHT) / 2, text: '', z: topZ() }, sty));
        } else {
          const s = 220;
          el = makeElement('note', { x: cur.bx - s / 2, y: cur.by - s / 2, w: s, h: s, text: '', z: topZ() }, sty);
        }
        if (!st.toolLock) setToolState('select');
        startEditing(el, true);
        return;
      }
      case 'press': {
        if (cur.toggleOff) {
          const ids = groupOf(cur.hit);
          setSel((s) => { const n = new Set(s); ids.forEach((id) => n.delete(id)); return n; });
        } else if (!e.shiftKey) {
          // a click (no drag) inside the frame picks what is under it, or nothing
          const hit = cur.hit || (cur.inFrame ? topHit(bx, by) : null);
          setSel(hit ? new Set(groupOf(hit)) : new Set());
        }
        return;
      }
      case 'press-empty':
        if (cur.lockedHit) setSel(new Set(groupOf(cur.lockedHit)));
        else if (!cur.shift) setSel(new Set());
        return;
      case 'marquee': case 'lasso': setOverlay(null); return;
      case 'move': {
        setCursor(null);
        const pv = model.preview;
        if (cancelled || !pv || (!cur.moved && !cur.dup)) { model.clearPreview(); return; }
        commit([...pv.values()]);
        return;
      }
      case 'resize': case 'rotate': case 'point': case 'leader': case 'dimend': case 'dimoff': {
        const pv = model.preview;
        if (cancelled || !pv) { model.clearPreview(); return; }
        commit([...pv.values()]);
        return;
      }
      case 'erase': {
        setOverlay(null);
        if (cur.eraserEnd) setPenErasing(false);
        const ids = [...cur.hit];
        setErasing(new Set());
        session.sendPresence({ erasing: [] });
        if (!cancelled && ids.length) commit(ids.map((id) => ({ id, deleted: true })));
        return;
      }
      default: return;
    }
  }

  function onDoubleClick(e) {
    const st = S.current;
    if (!st.canEdit || st.tool !== 'select') return;
    if (lastRole.current === 'finger' || lastRole.current === 'ignore') return;   // a hand or a finger's double tap writes nothing
    const [bx, by] = toBoard(e.clientX, e.clientY);
    const hit = topHit(bx, by);
    if (hit && HAS_TEXT.has(hit.type)) { startEditing(hit); return; }
    if (hit && hit.type === 'symbol') { setSel(new Set([hit.id])); setPanelFocus(hit.id); return; }
    if (hit && hit.type === 'image') { focusOn(hit); return; }
    if (!hit) {
      const sty = styleFor('text');
      const el = fitText(makeElement('text', { x: bx, y: by - (sty.fontSize * LINE_HEIGHT) / 2, text: '', z: topZ() }, sty));
      startEditing(el, true);
    }
  }

  // wheel: zoom with Ctrl (and a trackpad pinch); scroll moves the view in
  // full screen — inline, the page keeps its scroll unless Ctrl is held
  useEffect(() => {
    const el = svgRef.current; if (!el) return undefined;
    const onWheel = (e) => {
      const r = el.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const k = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0022));
        zoomAt(e.clientX - r.left, e.clientY - r.top, S.current.view.z * k);
        return;
      }
      if (!full) return;
      e.preventDefault();
      const mul = e.deltaMode === 1 ? 20 : 1;
      setView((v) => ({ ...v, x: v.x + (e.shiftKey ? e.deltaY : e.deltaX) * mul / v.z, y: v.y + (e.shiftKey ? 0 : e.deltaY) * mul / v.z }));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [full, zoomAt]);

  // ---- selection actions ---------------------------------------------------------------------------------
  const act = useMemo(() => {
    const chosen = () => S.current.elements.filter((el) => S.current.sel.has(el.id));
    return {
      remove({ confirmed = false } = {}) {
        const list = chosen().filter((el) => !el.locked || isDrawing(el));
        const ds = list.filter(isDrawing);
        const riders = ds.length ? ridersOf(S.current.elements, ds).filter((r) => !list.some((l) => l.id === r.id)) : [];
        if (ds.length && !confirmed) { setConfirmRemove({ ids: ds.map((d) => d.id), riders: riders.length }); return; }
        const gone = [...list, ...riders];
        if (gone.length) commit(gone.map((el) => ({ id: el.id, deleted: true })));
        setSel(new Set(chosen().filter((el) => el.locked && !isDrawing(el)).map((el) => el.id)));
      },
      exportDrawing() { const el = chosen().find(isDrawing); if (el) setExportOpen({ drawing: el.id }); },
      duplicate(offset = 16) {
        const list = chosen(); if (!list.length) return;
        const d = offset / S.current.view.z; const z0 = topZ(); const gids = new Map();
        const copies = list.map((el, i) => {
          let gid = el.groupId;
          if (gid) { if (!gids.has(gid)) gids.set(gid, newId()); gid = gids.get(gid); }
          const c = { ...moveBy(el, d, d), id: newId(), z: z0 + i, version: 0, groupId: gid };
          delete c.locked;
          return c;
        });
        commit(copies);
        setSel(new Set(copies.map((c) => c.id)));
      },
      lock() {
        const list = chosen(); if (!list.length) return;
        const all = list.every((el) => el.locked);
        commit(list.map((el) => { const n = { ...el }; if (all) delete n.locked; else n.locked = true; return n; }));
      },
      group() {
        const list = chosen(); if (list.length < 2) return;
        const gid = newId();
        commit(list.map((el) => ({ ...el, groupId: gid })));
      },
      ungroup() {
        const list = chosen().filter((el) => el.groupId); if (!list.length) return;
        commit(list.map((el) => { const n = { ...el }; delete n.groupId; return n; }));
      },
      front() {
        const list = chosen(); if (!list.length) return;
        const z0 = model.maxZ() + 1;
        commit(list.map((el, i) => ({ ...el, z: z0 + i })));
      },
      back() {
        const list = chosen(); if (!list.length) return;
        const z0 = model.minZ() - list.length - 1;
        commit(list.map((el, i) => ({ ...el, z: z0 + i })));
      },
      forward() {
        const all = S.current.elements; const list = chosen(); if (!list.length) return;
        const top = Math.max(...list.map((el) => all.indexOf(el)));
        const above = all.slice(top + 1).find((el) => !S.current.sel.has(el.id));
        if (!above) return;
        const next = all[all.indexOf(above) + 1];
        const zTop = next ? (above.z + next.z) / 2 : above.z + 1;
        commit(list.map((el, i) => ({ ...el, z: zTop + i * 1e-6 })));
      },
      backward() {
        const all = S.current.elements; const list = chosen(); if (!list.length) return;
        const low = Math.min(...list.map((el) => all.indexOf(el)));
        const below = all.slice(0, low).reverse().find((el) => !S.current.sel.has(el.id));
        if (!below) return;
        const prev = all[all.indexOf(below) - 1];
        const zLow = prev ? (below.z + prev.z) / 2 : below.z - 1;
        commit(list.map((el, i) => ({ ...el, z: zLow + i * 1e-6 })));
      },
      selectAll() { setSel(new Set(S.current.elements.map((el) => el.id))); },
      focus() { const el = chosen()[0]; if (el) focusOn(el); },
    };
  }, [commit, model, focusOn]);

  // a style picked: the next thing drawn looks like this — and so does the selection
  const onStyle = useCallback((patch, { live: isLive } = {}) => {
    const st = S.current;
    const stylePatch = { ...patch };
    if (patch.width != null) {
      delete stylePatch.width;
      if (patch.sizeKind === 'pen') stylePatch.penWidth = patch.width;
      else if (patch.sizeKind === 'highlighter') stylePatch.highlightWidth = patch.width;
      else stylePatch.width = patch.width;
      delete stylePatch.sizeKind;
    }
    if (patch.textColor) { stylePatch.stroke = patch.textColor; delete stylePatch.textColor; }
    if (!isLive) setStyle(stylePatch);
    const list = st.elements.filter((el) => st.sel.has(el.id) && !el.locked);
    if (!list.length) return;
    const changed = list.map((el) => {
      if (el.type === 'symbol') {
        let n = { ...el };
        if (patch.stroke) n.stroke = patch.stroke;
        if (patch.symbolSize && patch.symbolSize !== el.fontSize) { const [cx, cy] = center(el); const k = patch.symbolSize / el.fontSize; n = scaleWithin(n, cx, cy, k, k); }
        if (patch.opacity != null) n.opacity = patch.opacity;
        return n;
      }
      const n = { ...el };
      if (patch.stroke && (LINEAR.has(el.type) && el.type !== 'highlighter' || SHAPES.has(el.type))) { n.stroke = patch.stroke; if (n.text) n.textColor = patch.stroke; }
      if (patch.stroke && el.type === 'text') n.textColor = patch.stroke;
      if (patch.textColor && HAS_TEXT.has(el.type)) n.textColor = patch.textColor;
      if (patch.highlight && el.type === 'highlighter') n.stroke = patch.highlight;
      if (patch.fill && SHAPES.has(el.type)) n.fill = patch.fill;
      if (patch.noteColor && el.type === 'note') n.fill = patch.noteColor;
      if (patch.width != null && (LINEAR.has(el.type) || SHAPES.has(el.type))) n.width = patch.width;
      if (patch.dash && (SHAPES.has(el.type) || el.type === 'line' || el.type === 'arrow')) n.dash = patch.dash;
      if (patch.startHead && (el.type === 'line' || el.type === 'arrow')) n.startHead = patch.startHead;
      if (patch.endHead && (el.type === 'line' || el.type === 'arrow')) n.endHead = patch.endHead;
      if (HAS_TEXT.has(el.type) && (el.text || el.type === 'text' || el.type === 'note')) {
        if (patch.fontSize) n.fontSize = patch.fontSize;
        if (patch.font) n.font = patch.font;
        if (patch.bold !== undefined) { if (patch.bold) n.bold = true; else delete n.bold; }
        if (patch.align && el.type === 'text') n.align = patch.align;
      }
      if (patch.opacity != null) n.opacity = patch.opacity;
      return n.type === 'text' ? fitText(n) : n.type === 'note' ? fitNote(n) : n;
    });
    if (isLive) model.setPreview(new Map(changed.map((el) => [el.id, el])));
    else commit(changed);
  }, [commit, model, setStyle]);

  // ---- keyboard, clipboard, drop ---------------------------------------------------------------------------
  function onKeyDown(e) {
    const st = S.current;
    if (st.editing || e.target.closest?.('input, textarea, select, .wb-modal')) return;
    // the board's keys are the board's: the page around it (a pop-up that closes
    // on Esc, the site's own shortcuts) does not see them
    e.stopPropagation();
    const mod = e.ctrlKey || e.metaKey;
    const k = e.key.toLowerCase();
    if (picker && e.key === 'Escape') { setPicker(false); return; }
    if (k === ' ' && !spaceDown.current) { spaceDown.current = true; setCursor('grab'); e.preventDefault(); return; }
    if (mod && k === 'z') { e.preventDefault(); if (!st.canEdit) return; const ids = e.shiftKey ? model.redo() : model.undo(); if (ids) setSel(new Set(ids.filter((id) => model.els.has(id)))); return; }
    if (mod && k === 'y') { e.preventDefault(); if (!st.canEdit) return; const ids = model.redo(); if (ids) setSel(new Set(ids.filter((id) => model.els.has(id)))); return; }
    if (mod && k === 'a') { e.preventDefault(); act.selectAll(); return; }
    if (mod && k === 'd') { e.preventDefault(); if (st.canEdit) act.duplicate(); return; }
    if (mod && k === 'g') { e.preventDefault(); if (st.canEdit) (e.shiftKey ? act.ungroup : act.group)(); return; }
    if (mod && k === 'b') { e.preventDefault(); if (st.canEdit) onStyle({ bold: !st.style.bold }); return; }
    if (mod && (e.key === ']' || e.key === '}')) { e.preventDefault(); if (st.canEdit) (e.shiftKey ? act.front : act.forward)(); return; }
    if (mod && (e.key === '[' || e.key === '{')) { e.preventDefault(); if (st.canEdit) (e.shiftKey ? act.back : act.backward)(); return; }
    if (mod && (e.key === '=' || e.key === '+')) { e.preventDefault(); zoomBy(1.25); return; }
    if (mod && e.key === '-') { e.preventDefault(); zoomBy(0.8); return; }
    if (mod && e.key === '0') { e.preventDefault(); const { w, h } = st.size; zoomAt(w / 2, h / 2, 1); return; }
    if (mod) return;
    if (e.shiftKey && (e.key === '!' || e.code === 'Digit1')) { e.preventDefault(); fitTo(st.elements); return; }
    if (e.shiftKey && (e.key === ')' || e.code === 'Digit0')) { e.preventDefault(); const { w, h } = st.size; zoomAt(w / 2, h / 2, 1); return; }
    if (e.key === 'Delete' || e.key === 'Backspace') { if (st.canEdit && st.sel.size) { e.preventDefault(); act.remove(); } return; }
    if (e.key === 'Escape') {
      if (g.current) { cancelGesture(); return; }
      if (st.sel.size) { setSel(new Set()); return; }
      if (st.focusId) { exitFocus(); return; }
      if (st.tool !== 'select') { setToolState('select'); return; }
      if (full) onToggleFull(false);
      return;
    }
    if ((e.key === 'PageDown' || e.key === 'PageUp') && st.focusId) {
      e.preventDefault();
      const cur = st.elements.find((x) => x.id === st.focusId);
      if (cur?.doc) gotoPage(cur.doc.page + (e.key === 'PageDown' ? 1 : -1));
      return;
    }
    if (e.key === 'Enter' && st.sel.size === 1) {
      const el = st.elements.find((x) => st.sel.has(x.id));
      if (el && HAS_TEXT.has(el.type)) { e.preventDefault(); startEditing(el); }
      return;
    }
    if (e.key.startsWith('Arrow') && st.sel.size && st.canEdit) {
      e.preventDefault();
      const d = (e.shiftKey ? 10 : 1) / st.view.z;
      const dx = e.key === 'ArrowLeft' ? -d : e.key === 'ArrowRight' ? d : 0;
      const dy = e.key === 'ArrowUp' ? -d : e.key === 'ArrowDown' ? d : 0;
      const nudge = new Map(st.elements.filter((el) => st.sel.has(el.id) && (!el.locked || isDrawing(el))).map((el) => [el.id, el]));
      for (const r of ridersOf(st.elements, [...nudge.values()].filter(isDrawing))) nudge.set(r.id, r);
      commit([...nudge.values()].map((el) => moveBy(el, dx, dy)));
      return;
    }
    if (e.altKey) return;
    if (KEYMAP[k] && (st.canEdit || ['select', 'hand', 'laser', 'lasso'].includes(KEYMAP[k]))) { setTool(KEYMAP[k]); return; }
    if (SHAPE_KEYS[k] && st.canEdit) { setStyle({ shape: SHAPE_KEYS[k] }); setTool('shape'); }
  }
  function onKeyUp(e) {
    if (e.key === ' ') { spaceDown.current = false; setCursor(null); }
  }

  function onCopy(e, cut = false) {
    const st = S.current;
    if (st.editing || e.target.closest?.('input, textarea')) return;
    const list = st.elements.filter((el) => st.sel.has(el.id));
    if (!list.length) return;
    clipboard = list;
    pasteCount.current = 0;
    try { e.clipboardData.setData('text/plain', CLIP_MARK + JSON.stringify(list)); e.preventDefault(); } catch { /* the page clipboard still has it */ }
    if (cut && st.canEdit) act.remove();
  }
  function pasteElements(list) {
    if (!list?.length) return;
    pasteCount.current += 1;
    const v = S.current.view; const { w, h } = S.current.size;
    const b = boundsOf(list);
    // in view? paste beside the original; else in the middle of the view
    const vb = { x: v.x, y: v.y, w: w / v.z, h: h / v.z };
    let dx; let dy;
    if (b && boxesOverlap(b, vb)) { dx = (20 * pasteCount.current) / v.z; dy = dx; } else {
      dx = vb.x + vb.w / 2 - (b.x + b.w / 2); dy = vb.y + vb.h / 2 - (b.y + b.h / 2);
    }
    const z0 = topZ(); const gids = new Map();
    const copies = list.map((el, i) => {
      let gid = el.groupId;
      if (gid) { if (!gids.has(gid)) gids.set(gid, newId()); gid = gids.get(gid); }
      const c = { ...moveBy(el, dx, dy), id: newId(), z: z0 + i, version: 0, groupId: gid };
      delete c.locked;
      return c;
    });
    commit(copies);
    setSel(new Set(copies.map((c) => c.id)));
  }
  function onPaste(e) {
    const st = S.current;
    if (!st.canEdit || st.editing || e.target.closest?.('input, textarea')) return;
    const dt = e.clipboardData;
    const pics = [...(dt?.files || [])].filter((f) => /^image\//.test(f.type));
    if (pics.length) { e.preventDefault(); placeFiles(pics); return; }
    const txt = dt?.getData('text/plain') || '';
    if (txt.startsWith(CLIP_MARK)) {
      e.preventDefault();
      try { pasteElements(JSON.parse(txt.slice(CLIP_MARK.length))); } catch { pasteElements(clipboard); }
      return;
    }
    if (txt.trim()) {
      e.preventDefault();
      const v = st.view; const { w, h } = st.size;
      const sty = styleFor('text');
      const el = fitText(makeElement('text', { x: v.x + w / 2 / v.z - 100 / v.z, y: v.y + h / 2 / v.z, text: txt.slice(0, 20000), z: topZ() }, sty));
      commit([el]);
      setSel(new Set([el.id]));
    }
  }
  function onDrop(e) {
    if (!S.current.canEdit) return;
    const list = [...(e.dataTransfer?.files || [])];
    if (!list.length) return;
    e.preventDefault();
    placeFiles(list, toBoard(e.clientX, e.clientY));
  }

  // ---- what the style bar is about -------------------------------------------------------------------------------
  const styleKinds = useMemo(() => {
    if (selected.length) return new Set(selected.map((e) => e.type));
    if (tool === 'pen' || tool === 'highlighter' || tool === 'line' || tool === 'arrow' || tool === 'text' || tool === 'note') return new Set([tool]);
    if (tool === 'shape') return new Set([style.shape]);
    if (tool === 'symbol') return new Set(['symbol']);
    return new Set();
  }, [selected, tool, style.shape]);
  const styleValues = useMemo(() => {
    if (selected.length) {
      const el = selected.find((e) => !e.locked) || selected[0];
      return {
        stroke: el.type === 'text' ? el.textColor : el.stroke, fill: el.fill, width: el.width, dash: el.dash, fontSize: el.fontSize,
        font: el.font, bold: el.bold, align: el.align, noteColor: el.type === 'note' ? el.fill : undefined, highlight: el.type === 'highlighter' ? el.stroke : undefined,
        startHead: el.startHead, endHead: el.endHead, opacity: el.opacity, textColor: el.textColor,
        symbolSize: el.type === 'symbol' ? el.fontSize : undefined,
      };
    }
    const width = tool === 'pen' ? style.penWidth : tool === 'highlighter' ? style.highlightWidth : style.width;
    return { ...style, width, textColor: style.stroke };
  }, [selected, style, tool]);

  // ---- render ---------------------------------------------------------------------------------------------------------
  const peers = [...live.peers.values()];
  const remoteDrafts = peers.flatMap((p) => (p.drafts || []).map((d) => ({ ...d, _peer: p })));
  const remoteErasing = new Set(peers.flatMap((p) => p.erasing || []));
  const allErasing = remoteErasing.size ? new Set([...erasing, ...remoteErasing]) : erasing;
  const editingEl = editing ? model.get(editing.id) : null;
  const boardStyle = {
    '--wb-rail-cols': railCols,
    height: full ? undefined : height,
    cursor: penErasing ? 'cell' : cursor || (['pen', 'highlighter', 'shape', 'line', 'arrow', 'text', 'note', 'lasso', 'symbol'].includes(tool) ? 'crosshair' : tool === 'eraser' ? 'cell' : tool === 'laser' ? 'pointer' : tool === 'hand' ? 'grab' : 'default'),
  };
  const statusText = {
    saved: 'Saved', saving: 'Saving…', uploading: 'Uploading picture…', offline: 'Not saved — trying again', error: 'Not saved', readonly: 'View only',
  }[live.status] || '';
  const statusTip = live.status === 'readonly'
    ? 'You may look and point with the laser, not draw — you may not edit this work item.'
    : live.status === 'offline' || live.status === 'error' ? (live.error || 'The server did not answer.')
      : live.status === 'saved' ? 'Every change is saved as you draw' : 'Your latest changes are on their way';
  const toolSide = narrow ? 'top' : 'right';
  const empty = !elements.length && !draft && !editing;
  const focusEl = focusId ? elements.find((x) => x.id === focusId) || null : null;
  const panelEl = canEdit && selected.length === 1 && selected[0].type === 'symbol' && !selected[0].locked ? selected[0] : null;
  const logoSrc = laf.logoDataUri || productLogo;

  return (
    <LayerContext.Provider value={layerRef}>
      {/* data-no-pull-refresh: a hand moving down the board never reloads the page (app-shell PullToRefresh) */}
      <div ref={rootRef} className={`wb-board ${full ? 'is-full' : 'is-inline'} ${narrow ? 'is-narrow' : ''} ${focusEl ? 'has-focus' : ''}`} style={boardStyle} tabIndex={0}
        data-no-pull-refresh="" onPointerDownCapture={notePen} onPointerMoveCapture={notePen} onPointerUpCapture={notePen}
        onKeyDown={onKeyDown} onKeyUp={onKeyUp} onCopy={(e) => onCopy(e)} onCut={(e) => onCopy(e, true)} onPaste={onPaste}
        onDragOver={(e) => { if (canEdit && e.dataTransfer?.types?.includes('Files')) e.preventDefault(); }} onDrop={onDrop}
        aria-label={`${fieldName || 'Whiteboard'}${workItem?.key ? ` of ${workItem.key}` : ''}`}>
        <div className="wb-paper" />
        <svg ref={svgRef} className="wb-canvas" onPointerDown={onPointerDown} onPointerMove={onPointerMove}
          onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onDoubleClick={onDoubleClick}
          onPointerLeave={() => { if (!g.current) { setHoverId(null); session.sendPresence({ away: true }); } }}>
          <defs>
            <filter id={shadowId} x="-10%" y="-10%" width="125%" height="130%">
              <feDropShadow dx="0" dy="3" stdDeviation="5" floodColor="#0f172a" floodOpacity="0.16" />
            </filter>
          </defs>
          <g transform={`scale(${view.z}) translate(${-view.x} ${-view.y})`}>
            <ElementsLayer elements={visible} files={files} filesRev={filesRev} erasing={allErasing} hideTextId={editingEl?.id} shadowId={shadowId} lod={view.z < 0.4} />
            {remoteDrafts.map((d) => <ElementView key={`r-${d._peer.sid}-${d.id}`} el={d} shadowId={shadowId} faded={false} />)}
            {draft && <ElementView el={draft} shadowId={shadowId} />}
          </g>
          {/* screen-space overlay */}
          {hoverId && (() => {
            const el = elements.find((x) => x.id === hoverId); if (!el) return null;
            const b = boxOf(el); const [x, y] = toScreen(b.x, b.y);
            return <rect className="wb-hover" x={x - 2} y={y - 2} width={b.w * view.z + 4} height={b.h * view.z + 4} rx="3" />;
          })()}
          {peers.map((p) => (p.sel || []).map((id) => {
            const el = elements.find((x) => x.id === id); if (!el) return null;
            const b = boxOf(el); const [x, y] = toScreen(b.x, b.y);
            return <rect key={`${p.sid}-${id}`} className="wb-peer-sel" style={{ stroke: p.color }} x={x - 4} y={y - 4} width={b.w * view.z + 8} height={b.h * view.z + 8} rx="4" />;
          }))}
          {focusEl && (() => {
            // the page in focus is lit; the rest of the board steps back
            const pts = corners(localBox(focusEl)).map(([x, y]) => toScreen(...toWorld(focusEl, x, y)));
            const hole = `M${pts.map((q) => q.join(' ')).join('L')}Z`;
            return (
              <g className="wb-spot" pointerEvents="none">
                <path d={`M-10 -10H${size.w + 10}V${size.h + 10}H-10Z${hole}`} fillRule="evenodd" />
                <path className="wb-spot-edge" d={hole} />
              </g>
            );
          })()}
          {frame && (frame.kind === 'box' ? (() => {
            const b = expandBox(frame.box, 6 / view.z);
            const pts = corners(b).map(([x, y]) => toScreen(...frameWorld(frame, x, y)).join(',')).join(' ');
            return <polygon className={`wb-frame ${frame.locked ? 'is-locked' : ''} ${frame.multi ? 'is-multi' : ''} ${frame.dim ? 'is-dim' : ''}`} points={pts} />;
          })() : <polyline className="wb-frame-line" points={frame.points.map((p) => toScreen(p[0], p[1]).join(',')).join(' ')} />)}
          {frame?.multi && selected.length > 1 && selected.map((el) => {
            const b = boxOf(el); const [x, y] = toScreen(b.x, b.y);
            return <rect key={el.id} className="wb-frame-member" x={x} y={y} width={b.w * view.z} height={b.h * view.z} />;
          })}
          {handles.map((h) => {
            if (h.id === 'rotate') return <g key={h.id}><line className="wb-rot-stem" x1={h.at[0]} y1={h.at[1]} x2={h.stem[0]} y2={h.stem[1]} /><circle className="wb-handle wb-handle-rot" cx={h.at[0]} cy={h.at[1]} r="6" /></g>;
            if (h.round) return <circle key={h.id} className={`wb-handle wb-handle-pt ${h.id === 'leader' ? 'is-leader' : ''}`} cx={h.at[0]} cy={h.at[1]} r="6" />;
            if (h.diamond) return <rect key={h.id} className="wb-handle wb-handle-off" x={h.at[0] - 5} y={h.at[1] - 5} width="10" height="10" transform={`rotate(45 ${h.at[0]} ${h.at[1]})`} />;
            return <rect key={h.id} className="wb-handle" x={h.at[0] - 5} y={h.at[1] - 5} width="10" height="10" rx="2" />;
          })}
          {frame?.locked && (() => {
            const b = boundsOf(selected); const [x, y] = toScreen(b.x + b.w, b.y);
            return <g transform={`translate(${x - 10} ${y - 26})`} className="wb-lock-badge"><rect width="20" height="20" rx="5" /><path d="M6 10h8v6H6zM7.5 10V8a2.5 2.5 0 015 0v2" fill="none" /></g>;
          })()}
          {overlay?.marquee && (() => {
            const [x, y] = toScreen(overlay.marquee.x, overlay.marquee.y);
            return <rect className="wb-marquee" x={x} y={y} width={overlay.marquee.w * view.z} height={overlay.marquee.h * view.z} />;
          })()}
          {overlay?.lasso && <polygon className="wb-lasso" points={overlay.lasso.map((p) => toScreen(p[0], p[1]).join(',')).join(' ')} />}
          {overlay?.eraser && overlay.eraser.length > 1 && <polyline className="wb-eraser-trail" points={overlay.eraser.map((p) => toScreen(p[0], p[1]).join(',')).join(' ')} />}
          {laser.length > 1 && <polyline className="wb-laser" points={laser.map((q) => toScreen(q.x, q.y).join(',')).join(' ')} />}
          {peers.map((p) => (p.laser && p.laser.length > 1
            ? <polyline key={`l-${p.sid}`} className="wb-laser" style={{ stroke: p.color }} points={p.laser.map((q) => toScreen(q[0], q[1]).join(',')).join(' ')} />
            : null))}
          {peers.map((p) => {
            if (p.away || p.x == null) return null;
            const [x, y] = toScreen(p.x, p.y);
            if (x < -20 || y < -20 || x > size.w + 20 || y > size.h + 20) return null;
            return (
              <g key={`c-${p.sid}`} className="wb-peer-cursor" transform={`translate(${x} ${y})`}>
                <path d="M0 0l11 6.2-4.6 1.3L4 12z" style={{ fill: p.color }} />
                <g transform="translate(10 13)">
                  <rect rx="4" height="18" width={Math.max(30, String(p.name || '').length * 6.4 + 12)} style={{ fill: p.color }} />
                  <text x="6" y="12.5">{p.name}</text>
                </g>
              </g>
            );
          })}
        </svg>

        {editingEl && <TextEditor el={editingEl} view={view} onChange={editText} onDone={finishEditing} />}

        {empty && (
          <div className="wb-empty" aria-hidden="true">
            <ShopZebra width={120} pose="sitting" />
            <div>{canEdit
              ? <>Pick the pen and start drawing — paste or drop in a picture or a PDF,<br />or let the zebra fetch a drawing from this work item.</>
              : 'Nothing on this whiteboard yet.'}</div>
          </div>
        )}

        <div className="wb-top">
          <div className="wb-title">
            {/* inline, the field's own label names it already */}
            {full && <span className="wb-title-name">{fieldName || 'Whiteboard'}</span>}
            {full && workItem?.key && <span className="wb-title-key">{workItem.key}{workItem.summary ? ` · ${workItem.summary}` : ''}</span>}
            <Tip label={statusTip}>
              <span className={`wb-status is-${live.status}`}>
                <Icon name={live.status === 'offline' || live.status === 'error' ? 'cloudOff' : 'cloud'} size={14} />{statusText}
              </span>
            </Tip>
          </div>
          <div className="wb-top-right">
            {peers.length > 0 && (
              <div className="wb-people">
                {peers.slice(0, 5).map((p) => (
                  <Tip key={p.sid} label={`${p.name} is on this whiteboard`}><Avatar name={p.name} color={p.color} ring /></Tip>
                ))}
                {peers.length > 5 && <span className="wb-people-more">+{peers.length - 5}</span>}
              </div>
            )}
            {canEdit && <>
              <IconButton icon="undo" label="Undo" keys="Ctrl Z" disabled={!model.canUndo()} onClick={() => { const ids = model.undo(); if (ids) setSel(new Set(ids.filter((id) => model.els.has(id)))); }} />
              <IconButton icon="redo" label="Redo" keys="Ctrl Y" disabled={!model.canRedo()} onClick={() => { const ids = model.redo(); if (ids) setSel(new Set(ids.filter((id) => model.els.has(id)))); }} />
            </>}
            <IconButton icon="download" label="Export as PNG or PDF" onClick={() => setExportOpen({ drawing: focusId })} disabled={!elements.length} />
            <Tip label={full ? 'Exit full screen' : 'Full screen'} keys={full ? 'Esc' : undefined}>
              <button type="button" className="wb-full-btn" onClick={() => onToggleFull(!full)}>
                <Icon name={full ? 'collapse' : 'expand'} size={16} />{full ? 'Exit full screen' : 'Full screen'}
              </button>
            </Tip>
          </div>
        </div>

        <div className="wb-rail-wrap">
          <ToolRail tool={penErasing ? 'eraser' : tool} setTool={setTool} shape={style.shape} setShape={(s) => setStyle({ shape: s })} canEdit={canEdit}
            symbol={style.symbol} setSymbol={(k) => setStyle({ symbol: k })}
            onImage={() => fileInput.current?.click()} onZebra={openDrawings} zebraRef={zebraBtn} workItemKey={workItem?.key}
            toolLock={toolLock} setToolLock={setToolLock} side={toolSide} cols={railCols} railRef={railRef} />
        </div>

        <div className="wb-style-wrap">
          <StyleBar kinds={styleKinds} values={styleValues} onChange={onStyle} canEdit={canEdit}
            selection={selected.length ? selected : null} actions={act} />
        </div>

        <div className="wb-zoom">
          <IconButton icon="minus" label="Zoom out" keys="Ctrl -" side="top" onClick={() => zoomBy(0.8)} />
          <Tip label="Zoom" side="top">
            <button ref={zoomBtn} type="button" className="wb-zoom-pct" onClick={() => setZoomOpen((o) => !o)}>{Math.round(view.z * 100)}%</button>
          </Tip>
          <Popover anchor={zoomBtn} open={zoomOpen} onClose={() => setZoomOpen(false)} side="top" align="end" className="wb-menu">
            <button type="button" onClick={() => { fitTo(elements); setZoomOpen(false); }}><Icon name="fit" size={16} />Zoom to fit<kbd>Shift 1</kbd></button>
            {selected.length > 0 && <button type="button" onClick={() => { fitTo(selected, 2); setZoomOpen(false); }}><Icon name="fit" size={16} />Zoom to selection</button>}
            {[0.5, 1, 2].map((zz) => (
              <button key={zz} type="button" onClick={() => { zoomAt(size.w / 2, size.h / 2, zz); setZoomOpen(false); }}><span className="wb-menu-pad" />{zz * 100}%{zz === 1 && <kbd>Shift 0</kbd>}</button>
            ))}
          </Popover>
          <IconButton icon="plus" label="Zoom in" keys="Ctrl +" side="top" onClick={() => zoomBy(1.25)} />
          {HAS_TOUCH && canEdit && <>
            <span className="wb-zoom-sep" />
            <IconButton icon={touchDraws ? 'touch' : 'touchOff'} side="top" active={touchDraws} onClick={() => setTouchDraws(!touchDraws)}
              label={touchDraws
                ? 'Fingers draw too — click so only the pen draws (a hand resting on the screen is then ignored)'
                : 'Only the pen draws — a hand resting on the screen is ignored, two fingers move and zoom. Click to draw with a finger too'} />
          </>}
        </div>

        {!full && onResizeHeight && (
          <div className="wb-resize-grip" role="separator" aria-label="Drag to make the whiteboard taller or shorter — double-click to fit it to the panel again"
            onDoubleClick={() => onResizeHeight(null)}
            onPointerDown={(e) => {
              e.preventDefault();
              const y0 = e.clientY; const h0 = height;
              const move = (ev) => onResizeHeight(Math.max(320, Math.min(1400, h0 + ev.clientY - y0)));
              const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
              window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
            }} />
        )}

        <input ref={fileInput} type="file" accept="image/*,application/pdf" multiple hidden
          onChange={(e) => { const list = [...(e.target.files || [])]; e.target.value = ''; if (list.length) placeFiles(list); }} />

        {toastMsg && <div className="wb-toast" role="status"><span>{toastMsg}</span><button type="button" aria-label="Close" onClick={() => setToastMsg(null)}><Icon name="close" size={14} /></button></div>}

        <div ref={layerRef} className="wb-layer" />

        {focusEl && (
          <div className="wb-focusbar" role="toolbar" aria-label="Focus">
            <span className="wb-fb-doc"><Icon name={isPdfDoc(focusEl) ? 'pdf' : 'image'} size={16} /><span>{focusEl.doc?.name || 'Picture'}</span></span>
            {focusEl.doc && focusEl.doc.pages > 1 && (
              <span className="wb-fb-pages">
                <IconButton icon="prev" label="Previous page" keys="PgUp" side="top" disabled={pageBusy || focusEl.doc.page <= 1} onClick={() => gotoPage(focusEl.doc.page - 1)} />
                <span className="wb-fb-page">{pageBusy ? <><span className="wb-spin" />Turning…</> : <>Page <b>{focusEl.doc.page}</b> of {focusEl.doc.pages}</>}</span>
                <IconButton icon="next" label="Next page" keys="PgDn" side="top" disabled={pageBusy || focusEl.doc.page >= focusEl.doc.pages} onClick={() => gotoPage(focusEl.doc.page + 1)} />
              </span>
            )}
            <button type="button" className="wb-fb-btn" aria-label={`Fit ${isPdfDoc(focusEl) ? 'page' : 'picture'}`} onClick={() => animateTo(viewFor(bounds(focusEl)))}><Icon name="focus" size={16} /><span className="wb-fb-label">Fit {isPdfDoc(focusEl) ? 'page' : 'picture'}</span></button>
            <button type="button" className="wb-fb-btn is-export" aria-label="Export the marked-up drawing" onClick={() => setExportOpen({ drawing: focusEl.id })}><Icon name="download" size={16} /><span className="wb-fb-label">Export drawing</span></button>
            <button type="button" className="wb-fb-btn" aria-label="Exit focus" onClick={exitFocus}><Icon name="close" size={16} /><span className="wb-fb-label">Exit focus</span></button>
          </div>
        )}

        {panelEl && !g.current && (
          <SymbolPanel key={panelEl.id} el={panelEl} focusFirst={panelFocus === panelEl.id}
            pos={panelPos} onMove={movePanel} boardRef={rootRef}
            onLive={(params) => model.setPreview(new Map([[panelEl.id, fitSymbol({ ...(model.els.get(panelEl.id) || panelEl), params })]]))}
            onSave={(params) => {
              // compare with what is SAVED (the screen may show a live preview of the same words)
              setPanelFocus(null);
              const base = model.els.get(panelEl.id);
              if (!base) { model.clearPreview(); return; }
              if (JSON.stringify(base.params || {}) === JSON.stringify(params || {})) { model.clearPreview(); return; }
              commit([fitSymbol({ ...base, params })]);
            }}
            onLeader={(on) => {
              // what is on screen (a caption still being typed included) keeps its words
              const base = model.get(panelEl.id) || panelEl;
              const fresh = makeElement('symbol', { kind: base.kind, params: base.params, fontSize: base.fontSize });
              const next = { ...base };
              if (on && fresh.leader) next.leader = fresh.leader; else delete next.leader;
              commit([fitSymbol(next)]);
            }}
            onClose={() => { setSel(new Set()); setPanelFocus(null); }} />
        )}

        {zebra && <ZebraRun from={zebra.from} to={zebra.to} onArrive={() => { setPicker({ top: zebra.top }); setZebra(null); }} />}
        {picker && (
          <div className="wb-picker-host" style={{ '--picker-top': `${picker.top || 90}px` }}>
            <AttachmentPicker workItemKey={workItem.key} fieldId={fieldId}
              onPlaced={(res, a) => { setPicker(false); placeFromServer(res, a.name); }}
              onAttached={onAttachmentAdded}
              onClose={() => { setPicker(false); rootRef.current?.focus({ preventScroll: true }); }} />
          </div>
        )}

        {confirmRemove && (() => {
          const ds = confirmRemove.ids.map((id) => elements.find((x) => x.id === id)).filter(Boolean);
          if (!ds.length) return null;
          const all = drawingsOf(elements);
          const what = ds.length === 1 ? drawingLabel(ds[0], all) : `${ds.length} drawings`;
          const close = () => { setConfirmRemove(null); rootRef.current?.focus({ preventScroll: true }); };
          return (
            <div className="wb-modal-scrim" onPointerDown={(e) => { if (e.target === e.currentTarget) close(); }}>
              <div className="wb-modal wb-confirm" role="alertdialog" aria-modal="true" aria-label="Remove from the whiteboard"
                onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } }}>
                <div className="wb-modal-head">
                  <div>
                    <div className="wb-modal-title">Remove {what} from the whiteboard?</div>
                    <div className="wb-modal-sub">
                      {confirmRemove.riders ? `The markup on ${ds.length === 1 ? 'it' : 'them'} (${confirmRemove.riders} item${confirmRemove.riders === 1 ? '' : 's'}) is removed with ${ds.length === 1 ? 'it' : 'them'}. ` : ''}
                      {`The file${ds.length === 1 ? ' stays' : 's stay'} attached to ${workItem?.key || 'the work item'}. Ctrl+Z puts ${ds.length === 1 ? 'it' : 'them'} back.`}
                    </div>
                  </div>
                </div>
                <div className="wb-modal-foot">
                  <button type="button" className="wb-text-btn" onClick={close}>Cancel</button>
                  <button type="button" className="wb-primary is-danger" autoFocus onClick={() => { setConfirmRemove(null); act.remove({ confirmed: true }); rootRef.current?.focus({ preventScroll: true }); }}>
                    <Icon name="trash" size={16} />Remove from the whiteboard
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {exportOpen && (
          <ExportDialog elements={elements} selected={selected} files={files} workItem={workItem} fieldName={fieldName}
            me={live.me?.name} logo={logoSrc} drawingId={exportOpen.drawing} onClose={() => { setExportOpen(null); rootRef.current?.focus({ preventScroll: true }); }} />
        )}
      </div>
    </LayerContext.Provider>
  );
}
